import { Flex, useBreakpointValue } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router'
import { useUserState } from '~/atoms/UserState'
import { EmptyState } from '~/components/EmptyState'
import { useAnalytics } from '~/hooks/useAnalytics'
import {
  useGetInfiniteMissedCalls,
  useUpdateMissedCall,
} from '~/queries/missedCallsQueries'
import { useGetPbxUsers } from '~/queries/userQueries'
import { AnalyticsNamespace } from '~/types/Analytics'
import { MissedCallsAnalyticsEvents } from '~/types/Analytics/MissedCallsAnalyticsEvents'
import { DateRange, SelectedFilterData } from '~/types/Shared'
import { SortDirection } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { CallsQueryParams } from '@mangovoice/types'

import {
  MissedCallFilterParams,
  tryGetQueryFilters,
} from './data/MissedCallFilterData'
import { MissedCallsTableColumn } from './data/MissedCallsTableData'
import { MissedCallsListViewHeader } from './MissedCallsListViewHeader'
import { MissedCallsTable } from './MissedCallsTable'
import { mapUiStatusToApiStatus, MissedCall } from './types/MissedCall'

// Stable empty object to prevent unnecessary re-renders when no filters are applied
const EMPTY_FILTER_PARAMS: MissedCallFilterParams = {}

export const MissedCallsPage = () => {
  const { t } = useTranslation()
  const { pbxId } = useUserState()
  const { track } = useAnalytics(AnalyticsNamespace.MISSED_CALLS)
  const { key: navigationKey } = useLocation()

  useEffect(() => {
    // nav_key makes each real navigation into the page a distinct payload, so
    // the analytics dedup layer keeps them; a remount inside one navigation
    // repeats the key and is dropped as the loop it is.
    track(MissedCallsAnalyticsEvents.PageViewed, { nav_key: navigationKey })
  }, [])

  const [dateTimeRange, setDateTimeRange] = useState<DateRange>()
  const [activeFilters, setActiveFilters] = useState<SelectedFilterData[]>([])

  // Memoize pbx_ids array to prevent unnecessary re-renders
  const pbxIds = useMemo(() => {
    return pbxId ? [pbxId] : []
  }, [pbxId])

  // Convert filters to query params
  // Normalize to ensure stable reference when filters are empty
  const filterParams = useMemo(() => {
    const params = tryGetQueryFilters(t, activeFilters)
    // Return a stable empty object if no filters are applied
    // Check if object has any keys (filters applied)
    return Object.keys(params).length > 0 ? params : EMPTY_FILTER_PARAMS
  }, [t, activeFilters])

  // Build query params with date range and filters
  const queryParams = useMemo(() => {
    const params: Partial<CallsQueryParams> & {
      missed_type?: string[]
      follow_up_users?: number[]
      missed_call_return_status?: string[]
      to_formatted?: string | string[]
    } = {
      pbx_ids: pbxIds,
      limit: 25,
      started_after: dateTimeRange?.after,
      started_before: dateTimeRange?.before,
    }

    // Add filter params if they exist
    if (filterParams.missed_type && filterParams.missed_type.length > 0) {
      // stringifyQueryParams will handle array formatting with comma separation
      params.missed_type = filterParams.missed_type
    }
    if (
      filterParams.missed_call_return_status &&
      filterParams.missed_call_return_status.length > 0
    ) {
      params.missed_call_return_status = filterParams.missed_call_return_status
    }
    if (
      filterParams.follow_up_users &&
      filterParams.follow_up_users.length > 0
    ) {
      params.follow_up_users = filterParams.follow_up_users
    }
    if (filterParams.to_formatted && filterParams.to_formatted.length > 0) {
      // Convert array to comma-separated string for API
      params.to_formatted = filterParams.to_formatted.join(',')
    }

    return params
  }, [pbxIds, dateTimeRange, filterParams])

  // Fetch missed calls from API
  const {
    results: missedCalls,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    totalCount,
    updateMissedCallInCache,
    refetch,
  } = useGetInfiniteMissedCalls(queryParams, {
    enabled: !!pbxId,
  })

  const { results: pbxUsers, isLoading: isLoadingPbxUsers } = useGetPbxUsers({
    pbxId: pbxId ?? 0,
    _queryParams: {
      limit: 200,
      ordering: ['full_name'],
    },
    options: {
      enabled: !!pbxId,
      staleTime: 5 * 60 * 1000,
    },
  })

  const updateMissedCallMutation = useUpdateMissedCall()

  // Handle follow-up status updates
  const handleFollowUpStatusChanged = useCallback(
    (missedCallId: number, newStatus: string) => {
      // Find the current missed call to get the current user assignment
      const currentMissedCall = missedCalls.find(
        (call) => call.id === missedCallId,
      )
      if (!currentMissedCall || !pbxId) {
        return
      }

      const apiStatus = mapUiStatusToApiStatus(newStatus)
      const originalStatus = currentMissedCall.follow_up_status ?? 'Open'
      const originalFollowUpTimeUtc =
        currentMissedCall.follow_up_time_utc ?? null

      // Optimistically update the cache (pass UI format, it will convert internally)
      // Also update follow_up_time_utc optimistically
      const currentTimeUtc = new Date().toISOString()
      updateMissedCallInCache(missedCallId, {
        follow_up_status: newStatus,
        follow_up_time_utc: currentTimeUtc,
      })

      // Send POST to API to persist the change
      updateMissedCallMutation.mutate(
        {
          call_id: missedCallId,
          follow_up_status: apiStatus,
          follow_up_user: currentMissedCall.assigned_to_user_id ?? null,
          follow_up_time_utc: currentTimeUtc,
          pbx: pbxId,
        },
        {
          onError: () => {
            // Rollback on error (pass UI format)
            updateMissedCallInCache(missedCallId, {
              follow_up_status: originalStatus,
              follow_up_time_utc: originalFollowUpTimeUtc,
            })
          },
        },
      )
    },
    [missedCalls, pbxId, updateMissedCallInCache, updateMissedCallMutation],
  )

  const handleAssigneeChanged = useCallback(
    (missedCallId: number, userId: number | null) => {
      // id, not just the assigned flag: the flag has two values, so assigning
      // two different calls inside the dedup window would collapse into one.
      track(MissedCallsAnalyticsEvents.AssigneeChanged, {
        id: missedCallId,
        assigned: userId !== null,
      })

      const currentMissedCall = missedCalls.find(
        (call) => call.id === missedCallId,
      )
      if (!currentMissedCall || !pbxId) {
        return
      }

      const originalUserId = currentMissedCall.assigned_to_user_id ?? null
      const originalStatus = currentMissedCall.follow_up_status ?? 'Open'
      const originalFollowUpTimeUtc =
        currentMissedCall.follow_up_time_utc ?? null
      const apiStatus = mapUiStatusToApiStatus(originalStatus)

      // Update follow_up_time_utc to current time when user assignment changes
      const currentTimeUtc = new Date().toISOString()

      // Optimistically update the cache with user assignment and current time
      const updates: Partial<MissedCall> = {
        assigned_to_user_id: userId,
        follow_up_time_utc: currentTimeUtc,
      }
      updateMissedCallInCache(missedCallId, updates)

      // If user is unassigned, reset status to Open
      const finalStatus = userId === null ? 'open' : apiStatus

      // If user is unassigned, also update the status in the UI optimistically
      if (userId === null) {
        updateMissedCallInCache(missedCallId, { follow_up_status: 'Open' })
      }

      // Send POST to API to persist the change
      updateMissedCallMutation.mutate(
        {
          call_id: missedCallId,
          follow_up_status: finalStatus,
          follow_up_user: userId,
          follow_up_time_utc: currentTimeUtc,
          pbx: pbxId,
        },
        {
          onError: () => {
            // Rollback on error - restore user assignment, status, and follow-up time
            updateMissedCallInCache(missedCallId, {
              assigned_to_user_id: originalUserId,
              follow_up_status: originalStatus,
              follow_up_time_utc: originalFollowUpTimeUtc,
            })
          },
        },
      )
    },
    [
      missedCalls,
      pbxId,
      updateMissedCallInCache,
      updateMissedCallMutation,
      track,
    ],
  )

  const screenSizeRef = useRef('md')
  const screenSize =
    useBreakpointValue(
      {
        base: 'xs',
        sm: 'sm',
        md: 'md',
        lg: 'lg',
        xl: 'xl',
        '2xl': 'xxl',
      },
      { ssr: false },
    ) ?? screenSizeRef.current
  screenSizeRef.current = screenSize

  let tableSize: Size = Size.lg
  if (screenSize === 'xs' || screenSize === 'sm') {
    tableSize = Size.sm
  } else if (screenSize === 'md') {
    tableSize = Size.md
  }

  const handleFilterDataChanged = (activeFilterData: SelectedFilterData[]) => {
    setActiveFilters(activeFilterData)
  }

  const handleDateTimeRangeChanged = (dateTimeRange: DateRange) => {
    setDateTimeRange(dateTimeRange)
  }

  const handleSortableColumnClicked = (
    _columnType: MissedCallsTableColumn,
    _currentSortDirection?: SortDirection,
  ) => {
    // Handle column sorting logic here
    // TODO: Implement column sorting
  }

  const handleEndReached = () => {
    // Load more data if available
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  return (
    <Flex
      flexDirection="row"
      width="100%"
      alignSelf="stretch"
      justifyItems="flex-start"
      alignItems="flex-start"
      position="relative"
    >
      <Flex
        direction="column"
        flex="1 0 0"
        alignSelf="stretch"
        alignItems="flex-start"
        minHeight="0"
      >
        <MissedCallsListViewHeader
          dateTimeRange={dateTimeRange}
          callCount={totalCount || 0}
          tableSize={tableSize}
          activeFilters={activeFilters}
          onActiveFiltersChanged={handleFilterDataChanged}
          onDateTimeRangeChanged={handleDateTimeRangeChanged}
          onRefresh={refetch}
          isRefreshing={isLoading}
        />
        {isError ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
            flex="1"
          >
            <EmptyState
              title={t('missedCalls.errorLoadingMissedCalls')}
              subtitle={
                (error as any)?.message || t('missedCalls.pleaseTryAgainLater')
              }
            />
          </Flex>
        ) : missedCalls.length === 0 && !isLoading ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
            flex="1"
          >
            <EmptyState
              title={t('missedCalls.emptyState')}
              subtitle={t('missedCalls.emptyStateSubtitle')}
            />
          </Flex>
        ) : (
          <MissedCallsTable
            missedCalls={missedCalls}
            isLoading={isLoading}
            tableSize={tableSize}
            columnSorting={[]}
            onSortableColumnClicked={handleSortableColumnClicked}
            onEndReached={handleEndReached}
            onFollowUpStatusChanged={handleFollowUpStatusChanged}
            assigneeUsers={pbxUsers ?? []}
            isAssigneeUsersLoading={isLoadingPbxUsers}
            onAssigneeChanged={handleAssigneeChanged}
          />
        )}
      </Flex>
      <Outlet />
    </Flex>
  )
}

export default MissedCallsPage
